import { Button, Footer, Header, NewsletterSignup } from "@/components/shared";
import {
  Building2,
  Eye,
  Globe,
  Lock,
  MapPin,
  Target,
  TrendingUp,
  X,
  Zap,
} from "lucide-react";
import { useEffect, useState } from "react";

interface PropertyDetails {
  propertyName: string;
  location: string;
  size: string;
  footfall: string;
  businessAge: string;
  currentOfferings: string;
}

interface ResultsOverviewProps {
  selectedBusiness: string;
  selectedChallenges: string[];
  propertyDetails: PropertyDetails;
}

interface TransformationStrategy {
  metric: string;
  label: string;
  timeframe: string;
  strategy: string;
}

export default function ResultsOverview({
  selectedBusiness,
  selectedChallenges,
  propertyDetails,
}: ResultsOverviewProps) {
  const [showLockedInsight, setShowLockedInsight] = useState<string | null>(
    null
  );
  const [insightIndex, setInsightIndex] = useState(0);

  const transformationStrategies: Record<string, TransformationStrategy> = {
    property: {
      metric: "47%",
      label: "Occupancy Increase",
      timeframe: "6 months",
      strategy:
        "Created a business community sub-brand with its own magazine, events, and cultural identity that transforms empty space into a thriving ecosystem",
    },
    hospitality: {
      metric: "73%",
      label: "Guest Satisfaction",
      timeframe: "4 months",
      strategy:
        "Repositioned as a creative cultural destination with curated experiences and community programming that guests actively seek out",
    },
    retail: {
      metric: "89%",
      label: "Repeat Visits",
      timeframe: "3 months",
      strategy:
        "Developed ongoing community programming and cultural events that extend beyond the physical space, creating lasting customer relationships",
    },
  };

  const result = transformationStrategies[selectedBusiness];

  const lockedInsights = [
    "Why should customers choose them? ● Cultural differentiation strategy identified",
    "What's their realistic CapEx budget? ● Budget indicators suggest £50-150k range",
    "What's the compelling value proposition? ● Premium positioning through authentic community",
    "Current market conditions impact? ● Post-pandemic experience economy shift analysed",
    "Who are key decision makers? ● Stakeholder influence mapping complete",
    "Timeline for results? ● Phased implementation pathway optimised",
    "Biggest transformation fears? ● Risk mitigation strategies developed",
    "Success measurement framework? ● KPI dashboard & ROI tracking ready",
  ];

  const trendSources = [
    { source: "Instagram Culture Lab", confidence: 92 },
    { source: "TikTok Trend Analytics", confidence: 87 },
    { source: "Pinterest Insights", confidence: 89 },
    { source: "Google Trends", confidence: 84 },
    { source: "Proprietary B2C Data", confidence: 96 },
  ];

  const culturalTrends = [
    {
      trend: "Hyper-local authenticity",
      strength: 87,
      growth: "+23%",
      location: "Urban cores",
    },
    {
      trend: "Sustainability storytelling",
      strength: 92,
      growth: "+31%",
      location: "All demographics",
    },
    {
      trend: "Community-first experiences",
      strength: 78,
      growth: "+18%",
      location: "Suburban hubs",
    },
    {
      trend: "Digital-physical bridges",
      strength: 84,
      growth: "+27%",
      location: "Mixed-use developments",
    },
    {
      trend: "Wellness-integrated spaces",
      strength: 89,
      growth: "+29%",
      location: "Residential areas",
    },
  ];

  const businessTypeLabels: Record<string, string> = {
    property: "Property Development",
    hospitality: "Hospitality",
    retail: "Retail",
  };

  const insights = [
    { text: "10,000+ ads hit consumers daily", IconComponent: Target },
    {
      text: "Cultural experiences drive 3x engagement",
      IconComponent: TrendingUp,
    },
    { text: "Sub-brands reduce CAC by 45%", IconComponent: Zap },
    { text: "Community-driven growth lasts 10x longer", IconComponent: Globe },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setInsightIndex((prev) => (prev + 1) % insights.length);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  const createEmailBody = () => {
    const challengesList = selectedChallenges.join(", ");
    return `Hi there,

I've just completed the Culture Lab analysis for my ${businessTypeLabels[
      selectedBusiness
    ].toLowerCase()} and I'm interested in professional implementation.

Property Details:
- Name: ${propertyDetails.propertyName}
- Location: ${propertyDetails.location}
- Size: ${propertyDetails.size}
- Business Age: ${propertyDetails.businessAge}
- Current Offerings: ${propertyDetails.currentOfferings}
- Key Challenges: ${challengesList}

The analysis suggests a ${result.timeframe} timeline for ${
      result.metric
    } ${result.label.toLowerCase()}, and I'd like to discuss the complete strategic implementation.

When would be a good time for a consultation?

Thanks,
[Your name]`;
  };

  const emailSubject = `Strategic Implementation Consultation - ${propertyDetails.propertyName}`;
  const emailHref = `mailto:hello@thecontentgroup.co?subject=${encodeURIComponent(
    emailSubject
  )}&body=${encodeURIComponent(createEmailBody())}`;

  if (!result) {
    return <div>Error: Invalid business type selected</div>;
  }

  return (
    <>
      <Header variant="default" />

      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 text-white relative overflow-hidden pt-20">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-blue-500/10 to-purple-500/10 animate-pulse"></div>
          <div className="absolute top-1/4 right-1/4 w-32 h-32 bg-blue-400 rounded-full filter blur-3xl opacity-20 animate-pulse delay-1000"></div>
          <div className="absolute bottom-1/3 left-1/3 w-24 h-24 bg-purple-400 rounded-full filter blur-3xl opacity-15 animate-pulse delay-2000"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12">
          <div className="text-center mb-8 sm:mb-12">
            <div className="mb-3 sm:mb-4">
              <div className="inline-flex items-center bg-slate-800 bg-opacity-50 backdrop-blur-sm rounded-full px-3 sm:px-4 py-2 border border-slate-600 border-opacity-50">
                {(() => {
                  const IconComponent = insights[insightIndex].IconComponent;
                  return (
                    <IconComponent className="w-3 h-3 sm:w-4 sm:h-4 mr-2 text-blue-400" />
                  );
                })()}
                <span className="text-xs sm:text-sm text-gray-300 animate-pulse">
                  {insights[insightIndex].text}
                </span>
              </div>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-4 sm:mb-6 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent px-2">
              Your Cultural Transformation Strategy
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-300 px-2">
              Based on your {businessTypeLabels[selectedBusiness].toLowerCase()}{" "}
              analysis
            </p>
          </div>

          <div className="bg-slate-800 bg-opacity-40 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-8 sm:mb-12 border border-slate-600 border-opacity-50 hover:bg-opacity-50 transition-all duration-300">
            <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4 flex items-center text-white">
              <Building2 className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-blue-400" />
              Property Analysis Summary
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 text-sm">
              <div className="p-2 rounded-lg bg-slate-700 bg-opacity-30">
                <span className="text-gray-400 text-xs uppercase tracking-wide block">
                  Property
                </span>
                <div className="font-semibold text-white mt-1 truncate">
                  {propertyDetails.propertyName}
                </div>
              </div>
              <div className="p-2 rounded-lg bg-slate-700 bg-opacity-30">
                <span className="text-gray-400 text-xs uppercase tracking-wide block">
                  Location
                </span>
                <div className="font-semibold text-white mt-1 truncate">
                  {propertyDetails.location}
                </div>
              </div>
              <div className="p-2 rounded-lg bg-slate-700 bg-opacity-30">
                <span className="text-gray-400 text-xs uppercase tracking-wide block">
                  Size
                </span>
                <div className="font-semibold text-white mt-1">
                  {propertyDetails.size}
                </div>
              </div>
              <div className="p-2 rounded-lg bg-slate-700 bg-opacity-30">
                <span className="text-gray-400 text-xs uppercase tracking-wide block">
                  Challenges
                </span>
                <div className="font-semibold text-blue-400 mt-1">
                  {selectedChallenges.length} identified
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl sm:rounded-3xl p-0.5 sm:p-1 mb-8 sm:mb-12 group hover:shadow-2xl hover:shadow-blue-500/20 transition-all duration-500">
            <div className="bg-slate-900 bg-opacity-60 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 group-hover:bg-opacity-50 transition-all duration-300">
              <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-4 sm:mb-6 flex items-center text-white">
                <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 mr-2 text-purple-400" />
                Transformation Strategy
              </h3>
              <p className="text-base sm:text-lg mb-6 sm:mb-8 leading-relaxed text-gray-200">
                {result.strategy}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 mb-6 sm:mb-8">
                <div className="text-center p-4 bg-slate-800 bg-opacity-30 rounded-xl group/metric hover:scale-105 transition-transform duration-300">
                  <div className="text-3xl sm:text-4xl lg:text-5xl font-black mb-2 bg-gradient-to-r from-green-400 to-blue-400 bg-clip-text text-transparent">
                    {result.metric}
                  </div>
                  <div className="text-sm text-gray-300">{result.label}</div>
                </div>
                <div className="text-center p-4 bg-slate-800 bg-opacity-30 rounded-xl group/metric hover:scale-105 transition-transform duration-300">
                  <div className="text-3xl sm:text-4xl lg:text-5xl font-black mb-2 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                    {result.timeframe}
                  </div>
                  <div className="text-sm text-gray-300">Timeline</div>
                </div>
              </div>

              <div className="text-center">
                <h4 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4 text-white">
                  Ready to Implement This Strategy?
                </h4>
                <p className="text-gray-300 mb-4 sm:mb-6 max-w-xl mx-auto text-sm sm:text-base">
                  Get the complete strategic analysis and professional
                  implementation for {propertyDetails.propertyName}.
                </p>

                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => window.open(emailHref, "_blank")}
                  className="w-full sm:w-auto bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-green-500/25 min-h-[48px]"
                >
                  Book Strategic Consultation
                </Button>
              </div>
            </div>
          </div>

          <div className="bg-slate-800 bg-opacity-40 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-8 sm:mb-12 border border-slate-600 border-opacity-50 hover:bg-opacity-50 transition-all duration-300">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
              <h3 className="text-lg sm:text-xl font-bold flex items-center text-white">
                <MapPin className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-blue-400" />
                Cultural Trend Analysis
              </h3>
              <div
                className="flex items-center text-blue-400 cursor-pointer hover:text-blue-300 transition-colors group/unlock self-start sm:self-auto"
                onClick={() => setShowLockedInsight("trends")}
              >
                <Eye className="w-3 h-3 sm:w-4 sm:h-4 mr-1 group-hover/unlock:scale-110 transition-transform" />
                <span className="text-sm">View Full Analysis</span>
                <Lock className="w-3 h-3 ml-1 group-hover/unlock:animate-pulse" />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <h4 className="text-sm text-gray-400 mb-3 uppercase tracking-wide">
                  Data Sources
                </h4>
                <div className="space-y-2">
                  {trendSources.slice(0, 3).map((source, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between text-sm hover:bg-slate-700 hover:bg-opacity-30 rounded px-2 py-1 transition-colors"
                    >
                      <span className="text-gray-300 truncate mr-2">
                        {source.source}
                      </span>
                      <span className="text-green-400 font-mono flex-shrink-0">
                        {source.confidence}%
                      </span>
                    </div>
                  ))}
                  <div className="text-xs text-gray-500 italic px-2">
                    + {trendSources.length - 3} more sources...
                  </div>
                </div>
              </div>
              <div>
                <h4 className="text-sm text-gray-400 mb-3 uppercase tracking-wide">
                  Key Trends
                </h4>
                <div className="space-y-2">
                  {culturalTrends.slice(0, 3).map((trend, index) => (
                    <div
                      key={index}
                      className="hover:bg-slate-700 hover:bg-opacity-30 rounded px-2 py-1 transition-colors"
                    >
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-gray-300 truncate mr-2">
                          {trend.trend}
                        </span>
                        <span className="text-blue-400 font-mono flex-shrink-0">
                          {trend.strength}%
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">
                        {trend.location} • {trend.growth}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-slate-800 bg-opacity-40 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-8 sm:mb-12 border border-slate-600 border-opacity-50 hover:bg-opacity-50 transition-all duration-300">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
              <h3 className="text-lg sm:text-xl font-bold flex items-center text-white">
                <Target className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-purple-400" />
                Strategic Business Insights
              </h3>
              <div
                className="flex items-center text-purple-400 cursor-pointer hover:text-purple-300 transition-colors group/unlock self-start sm:self-auto"
                onClick={() => setShowLockedInsight("strategic")}
              >
                <span className="text-sm">Unlock Full Insights</span>
                <Lock className="w-3 h-3 sm:w-4 sm:h-4 ml-2 group-hover/unlock:animate-bounce" />
              </div>
            </div>

            <div className="space-y-2 sm:space-y-3 opacity-60 hover:opacity-70 transition-opacity">
              {lockedInsights.slice(0, 6).map((insight, index) => (
                <div
                  key={index}
                  className="flex items-start text-sm text-gray-400 hover:text-gray-300 transition-colors"
                >
                  <Lock className="w-3 h-3 mr-3 text-gray-500 flex-shrink-0 mt-0.5" />
                  <span className="leading-relaxed">{insight}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl sm:rounded-3xl p-0.5 sm:p-1 mb-8 sm:mb-12 group hover:shadow-2xl hover:shadow-purple-500/20 transition-all duration-500">
            <div className="bg-slate-900 bg-opacity-60 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 text-center group-hover:bg-opacity-50 transition-all duration-300">
              <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-3 sm:mb-4 flex items-center justify-center text-white">
                <Zap className="w-5 h-5 sm:w-6 sm:h-6 mr-2 text-yellow-400" />
                Unlock AI-Powered Intelligence
              </h3>
              <p className="text-gray-300 mb-4 sm:mb-6 max-w-2xl mx-auto text-sm sm:text-base">
                Get exclusive access to our AI bot for instant, personalised
                property transformation insights.
              </p>

              <div className="max-w-sm mx-auto">
                <NewsletterSignup
                  botAccess={true}
                  title="Get Exclusive Bot Access"
                  description="Join property professionals using AI insights"
                  source="culture-lab-completion"
                />
              </div>
            </div>
          </div>

          <div className="text-center mb-12">
            <p className="text-gray-400 mb-4 text-sm sm:text-base">
              Questions about your {propertyDetails.propertyName}{" "}
              transformation?
            </p>
            <Button
              variant="outline"
              size="md"
              onClick={() => window.open(emailHref, "_blank")}
              className="hover:bg-slate-700 hover:bg-opacity-50 transition-all duration-300 min-h-[48px] w-full sm:w-auto"
            >
              Send Direct Message
            </Button>
          </div>
        </div>

        <Footer variant="dark" />

        {showLockedInsight && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-300">
            <div className="bg-slate-800 bg-opacity-90 backdrop-blur-md rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 max-w-sm sm:max-w-md w-full border border-slate-600 border-opacity-50 animate-in slide-in-from-bottom-4 duration-300">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <Lock className="w-10 h-10 sm:w-12 sm:h-12 text-yellow-400 mb-3 sm:mb-4 animate-pulse" />
                  <h3 className="text-xl sm:text-2xl font-bold mb-2 text-white">
                    Unlock Full Analysis
                  </h3>
                </div>
                <button
                  onClick={() => setShowLockedInsight(null)}
                  className="text-gray-400 hover:text-white transition-colors hover:rotate-90 duration-300 p-1"
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>
              <p className="text-gray-300 mb-4 sm:mb-6 text-sm sm:text-base leading-relaxed">
                Get complete access to strategic insights, detailed trend
                analysis, and custom transformation roadmap for{" "}
                {propertyDetails.propertyName}.
              </p>

              <div className="space-y-3">
                <Button
                  variant="primary"
                  size="md"
                  className="w-full hover:scale-105 transition-transform duration-200 min-h-[48px]"
                  onClick={() => window.open(emailHref, "_blank")}
                >
                  Book Consultation Call
                </Button>
                <Button
                  variant="outline"
                  size="md"
                  className="w-full hover:bg-slate-700 hover:bg-opacity-50 transition-all duration-300 min-h-[48px]"
                  onClick={() => setShowLockedInsight(null)}
                >
                  Maybe Later
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
